<template>
  <div class="bch-home">
    <AppHeader />

    <main class="page-body">
      <div v-if="loading" class="home-loading">
        <i></i>
        Synchronizing BCH execution and settlement data...
      </div>

      <div v-else-if="loadError" class="home-error">
        <span>{{ loadError }}</span>
        <button @click="retryHomeOverview">Retry</button>
      </div>

      <section class="hero-premium">
        <div class="hero-grid-bg"></div>
        <div class="hero-glow-gold"></div>
        <div class="hero-glow-blue"></div>

        <div class="arbitrage-orbit">
          <div class="orbit-ring ring-a"></div>
          <div class="orbit-ring ring-b"></div>
          <div class="orbit-ring ring-c"></div>
          <div class="orbit-core">₿</div>
          <div class="orbit-dot dot-a"></div>
          <div class="orbit-dot dot-b"></div>
        </div>

        <div class="hero-content">
          <div class="hero-badge">
            <i></i>
            全球 BTC 多场所流动性执行基础设施
          </div>

          <h1>
            BCH 全球数字资产
            <span>智能执行与资产管理系统</span>
          </h1>

          <p class="hero-slogan">
            AI 市场分析 · 多场所智能路由 · USDT 统一结算 · BTC 资产交付
          </p>

          <p class="hero-desc">
            BCH 通过 AI 驱动的市场分析与执行引擎连接全球 BTC 流动性场所，持续评估报价、深度、滑点与风险参数，
            为用户提供标准化的数字资产策略配置、执行监控与结算记录。
          </p>

          <button class="primary-btn" @click="go('/market')">
            配置策略
          </button>

          <div class="hero-metrics">
            <div>
              <span>结算网络</span>
              <strong>BSC</strong>
            </div>
            <div>
              <span>策略计价资产</span>
              <strong>USDT</strong>
            </div>
            <div>
              <span>结算计价资产</span>
              <strong>USDT</strong>
            </div>
            <div>
              <span>资产交付</span>
              <strong>BTC</strong>
            </div>
          </div>

          <div class="risk-disclosure">
            <div class="risk-disclosure-icon">i</div>
            <div class="risk-disclosure-copy">
              <strong>市场风险披露</strong>
              <p>
                数字资产市场波动显著，策略执行结果会受到报价、流动性、滑点、网络状态与执行条件影响；
                页面数据不构成保本、固定回报或投资承诺。
              </p>
            </div>
            <span>RISK NOTICE</span>
          </div>
        </div>
      </section>

      <section class="engine-card">
        <div class="engine-head">
          <div>
            <h2>{{ engineHeadline }}</h2>
            <p>持续分析全球 BTC 报价、订单簿深度、滑点与执行路径</p>
          </div>

          <div class="engine-head-actions">
            <button
              class="engine-refresh"
              :class="{ rotating: refreshing }"
              :disabled="refreshing"
              @click="loadHomeOverview(true)"
            >
              ↻
            </button>
            <span :class="engineBadgeClass">{{ engineBadgeText }}</span>
          </div>
        </div>

        <div class="engine-route">
          <div>
            <small>买方执行场所</small>
            <strong>{{ currentTrade.buyExchange || 'Scanning' }}</strong>
          </div>
          <i></i>
          <div>
            <small>卖方执行场所</small>
            <strong>{{ currentTrade.sellExchange || 'Scanning' }}</strong>
          </div>
          <i></i>
          <div class="active">
            <small>报价差异</small>
            <strong>{{ spreadText }}</strong>
          </div>
        </div>

        <div class="engine-grid">
          <div>
            <b>{{ currentTrade.model || 'AI Strategy Engine' }}</b>
            <span>当前执行模型</span>
          </div>
          <div>
            <b>{{ riskSummary }}</b>
            <span>风险控制状态</span>
          </div>
          <div>
            <b>USDT</b>
            <span>结算计价资产</span>
          </div>
          <div>
            <b>{{ lastUpdatedText }}</b>
            <span>最后同步时间</span>
          </div>
        </div>
      </section>

      <section class="section-card invite-card">
        <div class="section-head">
          <div>
            <h2>合作网络中心</h2>
            <p>复制专属协作链接，管理 BCH 全球市场服务与合作网络。</p>
          </div>
          <span>Growth</span>
        </div>

        <div class="invite-link">
          {{ displayInviteLink }}
        </div>

        <button class="primary-btn invite-btn" @click="copyInviteLink">
          复制协作链接
        </button>

        <div class="invite-kpis">
          <div>
            <span>直接协作节点</span>
            <strong>{{ user.directCount }}</strong>
          </div>
          <div>
            <span>网络节点总数</span>
            <strong>{{ user.teamCount }}</strong>
          </div>
          <div>
            <span>网络业务规模</span>
            <strong>{{ money(user.teamPerformance) }}</strong>
          </div>
        </div>
      </section>

      <section class="stats-grid">
        <div class="stat-card">
          <span>累计策略配置</span>
          <strong>{{ money(user.totalDeposit) }}</strong>
          <em>USDT</em>
        </div>
        <div class="stat-card">
          <span>运行中策略资产</span>
          <strong>{{ money(user.activePrincipal) }}</strong>
          <em>USDT</em>
        </div>
        <div class="stat-card highlight">
          <span>今日已结算损益</span>
          <strong>{{ money(user.todayProfit) }}</strong>
          <em>USDT</em>
        </div>
        <div class="stat-card">
          <span>累计已结算损益</span>
          <strong>{{ money(user.totalProfit) }}</strong>
          <em>USDT</em>
        </div>
      </section>

      <section class="section-card">
        <div class="section-head">
          <div>
            <h2>AI BTC 策略资金配置</h2>
            <p>用户以 USDT 配置策略后，系统根据参数完成技术服务预算、执行额度管理与结算记录。</p>
          </div>
          <span>Model</span>
        </div>

        <div class="model-grid">
          <div class="model-card fee">
            <small>技术服务与基础设施</small>
            <strong>{{ percent(fundModel.modelFeeRate) }}</strong>
            <p>用于市场数据、模型计算、风险控制、执行接入与系统运维。</p>
          </div>

          <div class="model-card pool">
            <small>策略执行额度</small>
            <strong>{{ percent(fundModel.arbitragePoolRate) }}</strong>
            <p>用于跨市场交易执行、流动性调度及交易结果结算。</p>
          </div>
        </div>

        <!-- AI策略执行终端 -->
        <div class="execution-terminal">
          <div class="terminal-toolbar">
            <div class="terminal-dots">
              <i></i>
              <i></i>
              <i></i>
            </div>

            <div class="terminal-title">
              <strong>AI MARKET EXECUTION CONSOLE</strong>
              <span>BCH-ENGINE / MULTI-VENUE SMART ROUTING</span>
            </div>

            <div
              class="terminal-status"
              :class="{ offline: !engineOnline }"
            >
              <i></i>
              {{ terminalStatusText }}
            </div>
          </div>

          <div class="terminal-summary">
            <div>
              <small>Strategy Model</small>
              <strong>{{ currentTrade.model }}</strong>
            </div>
            <div>
              <small>Execution ID</small>
              <strong>{{ currentTrade.tradeNo }}</strong>
            </div>
            <div>
              <small>Risk Control</small>
              <strong :class="{ 'risk-pass': engineOnline }">
                {{ riskControlText }}
              </strong>
            </div>
          </div>

          <div class="professional-route">
            <div class="venue-card buy-venue">
              <div class="venue-top">
                <span>BUY VENUE</span>
                <em>{{ venueExecutionState }}</em>
              </div>
              <strong>{{ currentTrade.buyExchange }}</strong>
              <p>{{ currentTrade.symbol }}</p>

              <div class="venue-price">
                <small>Buy Price</small>
                <b>{{ price(currentTrade.buyPrice) }}</b>
                <span>USDT</span>
              </div>

              <div class="venue-volume">
                <span>{{ quantity(currentTrade.btcAmount, 6) }} BTC</span>
                <span>{{ money(currentTrade.capitalUsdt) }} USDT</span>
              </div>
            </div>

            <div class="route-core">
              <div class="route-line">
                <i></i>
                <span>AI</span>
                <i></i>
              </div>
              <small>Quote Differential</small>
              <strong>+{{ quantity(currentTrade.grossSpreadPct, 3) }}%</strong>
              <em>{{ currentTrade.latencyMs }} ms</em>
            </div>

            <div class="venue-card sell-venue">
              <div class="venue-top">
                <span>SELL VENUE</span>
                <em>{{ venueExecutionState }}</em>
              </div>
              <strong>{{ currentTrade.sellExchange }}</strong>
              <p>{{ currentTrade.symbol }}</p>

              <div class="venue-price">
                <small>Sell Price</small>
                <b>{{ price(currentTrade.sellPrice) }}</b>
                <span>USDT</span>
              </div>

              <div class="venue-volume">
                <span>{{ quantity(currentTrade.btcAmount, 6) }} BTC</span>
                <span>{{ money(currentTrade.sellAmountUsdt) }} USDT</span>
              </div>
            </div>
          </div>

          <div class="execution-metrics">
            <div>
              <span>Capital Deployed</span>
              <strong>{{ money(currentTrade.capitalUsdt) }}</strong>
              <em>USDT</em>
            </div>
            <div>
              <span>Trading Fees</span>
              <strong>-{{ money(currentTrade.feeUsdt) }}</strong>
              <em>USDT</em>
            </div>
            <div class="profit-metric">
              <span>Net Execution P&amp;L</span>
              <strong>+{{ money(currentTrade.netProfitUsdt) }}</strong>
              <em>USDT</em>
            </div>
            <div>
              <span>Execution Status</span>
              <strong>{{ executionStatusText }}</strong>
              <em>{{ currentTrade.settledAt }}</em>
            </div>
          </div>

          <div class="strategy-log">
            <div class="strategy-log-head">
              <div>
                <strong>Execution Audit Feed</strong>
                <span>AI execution, risk and settlement events</span>
              </div>
              <div class="log-streaming">
                <i></i>
                LIVE LOG
              </div>
            </div>

            <div class="log-window">
              <div
                v-for="(log, index) in engineLogs"
                :key="`${log.time}-${log.level}-${index}`"
                class="log-row"
              >
                <time>{{ log.time || '--:--:--.---' }}</time>
                <span class="log-level" :class="logLevelClass(log.level)">
                  {{ log.level || 'INFO' }}
                </span>
                <code>{{ log.message || '--' }}</code>
              </div>

              <div v-if="!engineLogs.length" class="log-empty">
                <strong>Waiting for verified execution events</strong>
                <span>Market, signal, risk, buy, sell and settlement logs will appear here.</span>
              </div>
            </div>
          </div>

          <div class="terminal-footer">
            <span>
              Engine node:
              <b>{{ currentTrade.engineNode || '--' }}</b>
            </span>
            <span>
              Market latency:
              <b>{{ currentTrade.latencyMs }} ms</b>
            </span>
            <span>
              Settlement:
              <b>USDT</b>
            </span>
          </div>
        </div>
      </section>

      <section class="quick-grid">
        <div class="quick-card" @click="go('/market')">
          <strong>策略配置中心</strong>
          <p>以 USDT 配置 BTC 多场所智能执行策略。</p>
        </div>
        <div class="quick-card" @click="go('/orders')">
          <strong>策略持仓</strong>
          <p>查看策略资产、已结算损益、赎回与运行状态。</p>
        </div>
        <div class="quick-card" @click="go('/redeem')">
          <strong>资产赎回</strong>
          <p>查看赎回周期、释放进度与到账记录。</p>
        </div>
        <div class="quick-card" @click="go('/experience')">
          <strong>标准体验方案</strong>
          <p>1000 USDT 固定额度策略配置。</p>
        </div>
      </section>

      <AppFooter />
    </main>

    <!-- 协作关系确认弹框：仅在 /bind-relation?ref=xxx 且当前用户未绑定时显示 -->
    <transition name="bind-modal-fade">
      <div
        v-if="showBindModal"
        class="bind-modal-mask"
        role="presentation"
        @touchmove.prevent
      >
        <div
          class="bind-relation-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bind-relation-title"
        >
          <div class="bind-dialog-grid"></div>
          <div class="bind-dialog-glow bind-dialog-glow-gold"></div>
          <div class="bind-dialog-glow bind-dialog-glow-blue"></div>

          <div class="bind-dialog-content">
            <div class="bind-dialog-icon">
              <span>₿</span>
              <i></i>
            </div>

            <div class="bind-dialog-badge">
              <i></i>
              BCH PARTNER NETWORK
            </div>

            <h3 id="bind-relation-title">确认协作关系</h3>
            <p class="bind-dialog-desc">
              系统检测到你正在通过专属协作链接访问 BCH。确认后，协作关系将写入系统且不可修改。
            </p>

            <div class="bind-address-field">
              <label>协作方地址</label>
              <div class="bind-address-input-wrap">
                <span class="bind-address-prefix">REF</span>
                <input
                  :value="shortPendingInviteAddress"
                  type="text"
                  readonly
                  tabindex="-1"
                  aria-label="协作方地址"
                />
                <span class="bind-address-lock">LOCKED</span>
              </div>
              <small>该地址来自当前协作链接，无法手动修改</small>
            </div>

            <div class="bind-security-note">
              <span>✓</span>
              <p>
                请确认协作来源真实可靠。点击确认后，将提交完整协作地址：
                <b>{{ shortPendingInviteAddress }}</b>
              </p>
            </div>

            <button
              class="bind-confirm-btn"
              :disabled="bindingInvite"
              @click="submitInviteBinding"
            >
              <span v-if="bindingInvite" class="bind-btn-spinner"></span>
              <span>{{ bindingInvite ? '正在确认关系...' : '确认协作关系' }}</span>
            </button>

            <p class="bind-dialog-footnote">
              One account · One relationship · Immutable record
            </p>
          </div>
        </div>
      </div>
    </transition>

    <!-- 页面内置提示，保证复制成功和绑定结果一定能够显示 -->
    <transition name="home-toast-fade">
      <div
        v-if="localToastVisible"
        class="home-local-toast"
        :class="`toast-${localToastType}`"
        role="status"
      >
        <span class="home-toast-icon">
          {{ localToastType === 'error' ? '!' : localToastType === 'warning' ? '!' : '✓' }}
        </span>
        <span>{{ localToastMessage }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import wallet from '@/store/wallet'
import { getBchHomeOverview } from '@/api/bchHome'
import { bindInviteAddress } from '@/api/overview'

const HOME_ROUTE_PATH = '/'
const BIND_RELATION_ROUTE_PATH = '/bind-relation'
const INVITE_ENTRY_ROUTE_PATHS = [HOME_ROUTE_PATH, BIND_RELATION_ROUTE_PATH]
const EVM_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/

const EMPTY_ENGINE = Object.freeze({
  running: false,
  tradeNo: '--',
  engineNode: '--',
  model: 'Awaiting verified strategy signal',
  riskLevel: '--',
  riskScore: '--',
  buyExchange: 'Scanning',
  sellExchange: 'Scanning',
  symbol: 'BTC/USDT',
  capitalUsdt: 0,
  btcAmount: 0,
  buyPrice: 0,
  sellPrice: 0,
  sellAmountUsdt: 0,
  grossSpreadRate: 0,
  grossSpreadPct: 0,
  feeUsdt: 0,
  netProfitUsdt: 0,
  latencyMs: 0,
  status: 'NO_DATA',
  settledAt: '--'
})

export default {
  name: 'HomeIndex',
  components: { AppHeader, AppFooter },
  data() {
    return {
      wallet,
      loading: true,
      refreshing: false,
      loadError: '',
      lastUpdatedAt: null,

      // 首页总览只在钱包连接成功后请求一次；记录已经加载的钱包，避免重复请求
      overviewLoadedWallet: '',

      user: {
        totalDeposit: 0,
        activePrincipal: 0,
        todayProfit: 0,
        totalProfit: 0,
        withdrawable: 0,
        relationBound: false,
        directCount: 0,
        teamCount: 0,
        teamPerformance: 0
      },

      fundModel: {
        modelFeeRate: 0.30,
        arbitragePoolRate: 0.70,
        systemDailyProfitRate: 0.003
      },

      engine: { ...EMPTY_ENGINE },
      engineLogs: [],

      // 协作关系确认状态
      overviewLoaded: false,
      showBindModal: false,
      pendingInviteAddress: '',
      bindingInvite: false,
      bindingCompletedLocally: false,
      invalidInviteRefNotified: '',

      // 页面内置 Toast，避免项目未安装 $toast/$message 时没有任何成功提示
      localToastVisible: false,
      localToastMessage: '',
      localToastType: 'success',
      localToastTimer: null,

      // 弹框打开时锁定页面滚动
      bodyScrollLocked: false,
      bodyOverflowBeforeModal: ''
    }
  },
  computed: {
    walletAddress() {
      return this.wallet?.state?.address || this.wallet?.state?.account || this.wallet?.state?.walletAddress || ''
    },
    inviteLink() {
      if (!this.walletAddress) return ''
      return `${window.location.origin}/#/?ref=${this.walletAddress}`
    },
    displayInviteLink() {
      if (!this.walletAddress) return '连接钱包后自动生成协作链接'
      return `${window.location.origin}/#/?ref=${this.shortAddress(this.walletAddress)}`
    },
    isInviteEntryRoute() {
      if (!this.$route) return false
      return INVITE_ENTRY_ROUTE_PATHS.includes(this.$route.path)
    },
    routeInviteAddress() {
      // 同时兼容：
      // 1. /#/bind-relation?ref=0x...
      // 2. /#/?ref=0x...
      if (!this.isInviteEntryRoute) return ''

      const rawRef = this.$route.query ? this.$route.query.ref : ''
      const refValue = Array.isArray(rawRef) ? rawRef[0] : rawRef

      return typeof refValue === 'string' ? refValue.trim() : ''
    },
    shortPendingInviteAddress() {
      return this.shortRelationAddress(this.pendingInviteAddress)
    },
    currentTrade() {
      return this.engine || { ...EMPTY_ENGINE }
    },
    engineOnline() {
      return Boolean(
        this.currentTrade &&
        this.currentTrade.status &&
        this.currentTrade.status !== 'NO_DATA'
      )
    },
    engineHeadline() {
      if (this.currentTrade.running) return 'AI 市场执行引擎运行中'
      if (this.engineOnline) return 'AI 市场执行引擎在线监控'
      return 'AI 市场执行引擎等待有效信号'
    },
    engineBadgeText() {
      if (this.refreshing || this.loading) return 'SYNC'
      if (this.currentTrade.running) return 'LIVE'
      if (this.engineOnline) return 'ONLINE'
      return 'STANDBY'
    },
    engineBadgeClass() {
      return {
        syncing: this.refreshing || this.loading,
        standby: !this.engineOnline
      }
    },
    terminalStatusText() {
      if (this.refreshing || this.loading) return 'SYNCING'
      if (this.currentTrade.running) return 'STREAMING'
      if (this.engineOnline) return 'CONNECTED'
      return 'STANDBY'
    },
    venueExecutionState() {
      const status = String(this.currentTrade.status || '').toUpperCase()
      if (status === 'SETTLED' || status === 'SUCCESS') return 'FILLED'
      if (status === 'RUNNING' || status === 'PROCESSING') return 'EXECUTING'
      return 'WAITING'
    },
    executionStatusText() {
      const status = String(this.currentTrade.status || 'NO_DATA').toUpperCase()
      const statusMap = {
        NO_DATA: 'AWAITING DATA',
        PENDING: 'PENDING REVIEW',
        RUNNING: 'IN EXECUTION',
        PROCESSING: 'IN EXECUTION',
        SETTLED: 'SETTLED',
        SUCCESS: 'SETTLED',
        FAILED: 'EXECUTION FAILED',
        FAIL: 'EXECUTION FAILED',
        CANCELLED: 'CANCELLED',
        CANCELED: 'CANCELLED',
        FROZEN: 'RISK HOLD'
      }

      return statusMap[status] || status
    },
    spreadText() {
      if (!this.engineOnline) return '--'
      return `+${this.quantity(this.currentTrade.grossSpreadPct, 3)}%`
    },
    riskSummary() {
      const score = this.currentTrade.riskScore || '--'
      const level = this.currentTrade.riskLevel || '--'
      return `${score} / ${level}`
    },
    riskControlText() {
      if (!this.engineOnline) return '-- / WAITING'
      return `${this.currentTrade.riskScore || '--'} / PASSED`
    },
    lastUpdatedText() {
      if (!this.lastUpdatedAt) return '--:--:--'
      return this.formatTime(this.lastUpdatedAt, false)
    }
  },
  watch: {
    walletAddress(newAddress, oldAddress) {
      const nextAddress = String(newAddress || '').trim().toLowerCase()
      const previousAddress = String(oldAddress || '').trim().toLowerCase()

      // 钱包断开后，允许下一次重新连接时重新加载。
      if (!nextAddress) {
        this.overviewLoaded = false
        this.overviewLoadedWallet = ''
        this.showBindModal = false
        this.pendingInviteAddress = ''
        this.loading = false
        return
      }

      // 只在首次连接或切换钱包时请求一次，不做轮询刷新。
      if (nextAddress !== previousAddress) {
        this.loadHomeOverviewOnce(false)
      }
    },
    '$route.fullPath'() {
      this.invalidInviteRefNotified = ''
      this.evaluateInviteBinding()
    },
    showBindModal(visible) {
      this.setBodyScrollLocked(visible)
    }
  },
  mounted() {
    // 钱包已经连接（例如刷新页面后自动恢复）时，只请求一次。
    if (this.walletAddress) {
      this.loadHomeOverviewOnce(false)
    } else {
      this.loading = false
    }
  },
  beforeDestroy() {
    if (this.localToastTimer) {
      window.clearTimeout(this.localToastTimer)
      this.localToastTimer = null
    }

    this.setBodyScrollLocked(false)
  },
  methods: {
    async loadHomeOverviewOnce(silent = false) {
      const addressKey = String(this.walletAddress || '').trim().toLowerCase()

      if (!addressKey) {
        this.loading = false
        return
      }

      // 当前钱包已经成功加载过，不再重复请求。
      if (this.overviewLoadedWallet === addressKey && this.overviewLoaded) {
        this.evaluateInviteBinding()
        return
      }

      await this.loadHomeOverview(silent, addressKey)
    },

    async loadHomeOverview(silent = false, requestAddressKey = '') {
      if (this.refreshing) return

      if (silent) {
        this.refreshing = true
      } else {
        this.loading = true
        this.loadError = ''
      }

      try {
        const response = await getBchHomeOverview({
          showLoading: false,
          showError: false
        })

        const payload = this.unwrapResponse(response)
        this.applyHomeOverview(payload)
        this.overviewLoaded = true
        this.overviewLoadedWallet = requestAddressKey || String(this.walletAddress || '').trim().toLowerCase()
        this.evaluateInviteBinding()
        this.lastUpdatedAt = new Date()
        this.loadError = ''
      } catch (error) {
        console.error('[BCH-HOME] overview request failed:', error)

        if (!silent) {
          this.loadError = 'BCH market data could not be synchronized.'
        }
      } finally {
        this.loading = false
        this.refreshing = false
      }
    },

    retryHomeOverview() {
      this.overviewLoaded = false
      this.overviewLoadedWallet = ''
      return this.loadHomeOverviewOnce(false)
    },

    unwrapResponse(response) {
      if (!response) return {}

      if (this.isOverviewPayload(response)) {
        return response
      }

      if (response.data && this.isOverviewPayload(response.data)) {
        return response.data
      }

      if (
        response.data &&
        response.data.data &&
        this.isOverviewPayload(response.data.data)
      ) {
        return response.data.data
      }

      return response.data || {}
    },

    isOverviewPayload(value) {
      return Boolean(
        value &&
        typeof value === 'object' &&
        (
          value.assets ||
          value.invite ||
          value.fundModel ||
          value.engine ||
          value.logs
        )
      )
    },

    applyHomeOverview(payload = {}) {
      const assets = payload.assets || {}
      const invite = payload.invite || {}
      const fundModel = payload.fundModel || {}
      const engine = payload.engine || {}

      this.user = {
        totalDeposit: this.numberValue(assets.totalDeposit),
        activePrincipal: this.numberValue(assets.activePrincipal),
        todayProfit: this.numberValue(assets.todayProfit),
        totalProfit: this.numberValue(assets.totalProfit),
        withdrawable: this.numberValue(assets.withdrawable),
        relationBound: this.booleanValue(invite.relationBound),
        directCount: this.integerValue(invite.directCount),
        teamCount: this.integerValue(invite.teamCount),
        teamPerformance: this.numberValue(invite.teamPerformance)
      }

      this.fundModel = {
        modelFeeRate: this.rateValue(fundModel.modelFeeRate, 0.30),
        arbitragePoolRate: this.rateValue(fundModel.arbitragePoolRate, 0.70),
        systemDailyProfitRate: this.rateValue(fundModel.systemDailyProfitRate, 0.003)
      }

      this.engine = {
        ...EMPTY_ENGINE,
        ...engine,
        running: Boolean(engine.running),
        capitalUsdt: this.numberValue(engine.capitalUsdt),
        btcAmount: this.numberValue(engine.btcAmount),
        buyPrice: this.numberValue(engine.buyPrice),
        sellPrice: this.numberValue(engine.sellPrice),
        sellAmountUsdt: this.numberValue(engine.sellAmountUsdt),
        grossSpreadRate: this.numberValue(engine.grossSpreadRate),
        grossSpreadPct: this.resolveSpreadPct(engine),
        feeUsdt: this.numberValue(engine.feeUsdt),
        netProfitUsdt: this.numberValue(engine.netProfitUsdt),
        latencyMs: this.integerValue(engine.latencyMs),
        status: engine.status || 'NO_DATA'
      }

      this.engineLogs = Array.isArray(payload.logs)
        ? payload.logs
            .filter(item => item && item.message)
            .slice(0, 30)
            .map(item => ({
              time: item.time || '--:--:--.---',
              level: String(item.level || item.logLevel || 'INFO').toUpperCase(),
              logLevel: String(item.logLevel || 'INFO').toUpperCase(),
              message: item.message
            }))
        : []
    },

    evaluateInviteBinding() {
      // 必须等首页接口返回后，再根据 relationBound 判断是否弹框。
      if (!this.overviewLoaded) return

      // 兼容 /#/bind-relation?ref=... 与 /#/?ref=... 两种协作入口。
      if (!this.isInviteEntryRoute || !this.routeInviteAddress) {
        this.showBindModal = false
        this.pendingInviteAddress = ''
        return
      }

      const inviteAddress = this.routeInviteAddress

      if (!inviteAddress || !this.isValidEvmAddress(inviteAddress)) {
        this.showBindModal = false
        this.pendingInviteAddress = ''

        if (inviteAddress && this.invalidInviteRefNotified !== inviteAddress) {
          this.invalidInviteRefNotified = inviteAddress
          this.toast('协作链接中的地址格式不正确', 'error')
        }
        return
      }

      this.invalidInviteRefNotified = ''
      this.pendingInviteAddress = inviteAddress

      // 后台已绑定，或本次页面刚刚绑定成功，都不再重复弹出。
      if (this.user.relationBound || this.bindingCompletedLocally) {
        this.showBindModal = false
        this.replaceBindRouteWithHome()
        return
      }

      if (!this.bindingInvite) {
        this.showBindModal = true
      }
    },

    async submitInviteBinding() {
      if (this.bindingInvite) return

      const inviteAddress = String(this.pendingInviteAddress || '').trim()

      if (!this.isValidEvmAddress(inviteAddress)) {
        this.toast('协作地址格式不正确，请重新打开有效协作链接', 'error')
        return
      }

      if (
        this.walletAddress &&
        String(this.walletAddress).toLowerCase() === inviteAddress.toLowerCase()
      ) {
        this.toast('不能将自己的钱包地址设置为协作方', 'error')
        return
      }

      this.bindingInvite = true

      try {
        await bindInviteAddress(inviteAddress, {
          showLoading: false,
          showError: false,
          successMsg: ''
        })

        this.bindingCompletedLocally = true
        this.user = {
          ...this.user,
          relationBound: true
        }
        this.showBindModal = false

        this.toast('协作关系确认成功', 'success')
        this.replaceBindRouteWithHome()

        // 本地状态已经更新，不再额外请求首页接口。
        // 页面下次重新连接钱包或重新进入时，再从服务端读取最新状态。
      } catch (error) {
        console.error('[BCH-HOME] bind invite failed:', error)
        this.toast(
          this.resolveRequestError(error, '协作关系确认失败，请稍后重试'),
          'error'
        )
      } finally {
        this.bindingInvite = false
      }
    },

    replaceBindRouteWithHome() {
      if (!this.$route || !this.isInviteEntryRoute) return

      const currentQuery = { ...(this.$route.query || {}) }
      if (!Object.prototype.hasOwnProperty.call(currentQuery, 'ref')) return

      delete currentQuery.ref

      const navigation = this.$router.replace({
        path: HOME_ROUTE_PATH,
        query: currentQuery
      })

      if (navigation && typeof navigation.catch === 'function') {
        navigation.catch(() => {})
      }
    },

    isValidEvmAddress(address) {
      return EVM_ADDRESS_PATTERN.test(String(address || '').trim())
    },

    resolveRequestError(error, fallback) {
      return (
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.data?.msg ||
        error?.data?.message ||
        error?.msg ||
        error?.message ||
        fallback
      )
    },

    setBodyScrollLocked(locked) {
      if (typeof document === 'undefined' || !document.body) return

      if (locked && !this.bodyScrollLocked) {
        this.bodyOverflowBeforeModal = document.body.style.overflow || ''
        document.body.style.overflow = 'hidden'
        this.bodyScrollLocked = true
        return
      }

      if (!locked && this.bodyScrollLocked) {
        document.body.style.overflow = this.bodyOverflowBeforeModal
        this.bodyOverflowBeforeModal = ''
        this.bodyScrollLocked = false
      }
    },

    resolveSpreadPct(engine = {}) {
      const directPct = this.numberValue(engine.grossSpreadPct)
      if (directPct > 0) return directPct

      const rate = this.numberValue(engine.grossSpreadRate)
      return rate > 0 ? rate * 100 : 0
    },

    numberValue(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },

    booleanValue(value) {
      return value === true || value === 1 || value === '1' || value === 'true'
    },

    integerValue(value) {
      return Math.max(0, Math.trunc(this.numberValue(value)))
    },

    rateValue(value, fallback) {
      const number = Number(value)
      return Number.isFinite(number) && number >= 0 ? number : fallback
    },

    go(path) {
      this.$router.push(path)
    },
    money(val) {
      return this.numberValue(val).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    price(val) {
      const number = this.numberValue(val)
      if (!number) return '--'
      return number.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    quantity(val, digits = 4) {
      return this.numberValue(val).toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      })
    },
    percent(rate) {
      return `${this.quantity(this.numberValue(rate) * 100, 0)}%`
    },
    shortAddress(addr) {
      if (!addr) return '--'
      return `${addr.slice(0, 6)}...${addr.slice(-4)}`
    },
    shortRelationAddress(addr) {
      if (!addr) return '--'
      const value = String(addr)
      if (value.length <= 14) return value
      return `${value.slice(0, 8)}..${value.slice(-4)}`
    },
    logLevelClass(level) {
      const normalized = String(level || 'INFO')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '')

      return `level-${normalized || 'info'}`
    },
    formatTime(date, withMilliseconds = true) {
      const value = date instanceof Date ? date : new Date(date)
      if (Number.isNaN(value.getTime())) return '--:--:--'

      const hh = String(value.getHours()).padStart(2, '0')
      const mm = String(value.getMinutes()).padStart(2, '0')
      const ss = String(value.getSeconds()).padStart(2, '0')

      if (!withMilliseconds) return `${hh}:${mm}:${ss}`

      const ms = String(value.getMilliseconds()).padStart(3, '0')
      return `${hh}:${mm}:${ss}.${ms}`
    },
    copyInviteLink() {
      if (!this.inviteLink) {
        this.toast('请先连接钱包', 'warning')
        return
      }

      this.copyText(this.inviteLink, '协作链接已复制')
    },

    async copyText(text, successMessage) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
          this.toast(successMessage, 'success')
          return
        }

        const copied = this.legacyCopyText(text)
        if (!copied) throw new Error('legacy copy returned false')

        this.toast(successMessage, 'success')
      } catch (error) {
        console.error('[BCH-HOME] copy failed:', error)
        this.toast('复制失败，请长按链接手动复制', 'error')
      }
    },

    legacyCopyText(text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      textarea.style.top = '0'
      textarea.style.opacity = '0'

      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      textarea.setSelectionRange(0, textarea.value.length)

      let copied = false
      try {
        copied = document.execCommand('copy')
      } finally {
        document.body.removeChild(textarea)
      }

      return copied
    },

    toast(message, type = 'success') {
      if (!message) return

      if (this.localToastTimer) {
        window.clearTimeout(this.localToastTimer)
        this.localToastTimer = null
      }

      this.localToastMessage = String(message)
      this.localToastType = type
      this.localToastVisible = true

      this.localToastTimer = window.setTimeout(() => {
        this.localToastVisible = false
        this.localToastTimer = null
      }, type === 'error' ? 3600 : 2400)
    }
  }
}
</script>

<style lang="less" scoped>
.bch-home {
  min-height: 100vh;
  background:
    radial-gradient(circle at 50% -8%, rgba(212, 175, 55, .18), transparent 28%),
    radial-gradient(circle at 100% 18%, rgba(35, 113, 255, .18), transparent 32%),
    linear-gradient(180deg, #05070d 0%, #070d18 46%, #0b1220 100%);
  color: #fff;
}

.page-body {
  padding: 0 12px 28px;
}

.hero-premium {
  position: relative;
  overflow: hidden;
  min-height: 520px;
  padding: 30px 18px 20px;
  border-radius: 0 0 34px 34px;
  background:
    radial-gradient(circle at 76% 20%, rgba(212,175,55,.24), transparent 28%),
    radial-gradient(circle at 20% 80%, rgba(37,99,235,.28), transparent 40%),
    linear-gradient(145deg, #05070d 0%, #09111f 50%, #111827 100%);
  border: 1px solid rgba(255,255,255,.08);
  border-top: none;
  box-shadow: 0 26px 72px rgba(0,0,0,.48);
}

.hero-grid-bg {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,.032) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.032) 1px, transparent 1px);
  background-size: 30px 30px;
  opacity: .72;
}

.hero-glow-gold,
.hero-glow-blue {
  position: absolute;
  border-radius: 50%;
  filter: blur(48px);
}

.hero-glow-gold {
  width: 220px;
  height: 220px;
  right: -90px;
  top: 80px;
  background: rgba(212,175,55,.26);
}

.hero-glow-blue {
  width: 240px;
  height: 240px;
  left: -100px;
  bottom: -90px;
  background: rgba(37,99,235,.26);
}

.hero-content {
  position: relative;
  z-index: 5;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  color: #ffe08a;
  background: rgba(212,175,55,.12);
  border: 1px solid rgba(212,175,55,.34);
}

.hero-badge i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #facc15;
  box-shadow: 0 0 14px rgba(250,204,21,.9);
}

.hero-premium h1 {
  margin: 22px 0 0;
  font-size: 39px;
  line-height: 1.08;
  font-weight: 950;
  letter-spacing: -1.5px;
  color: #fff;
}

.hero-premium h1 span {
  display: block;
  margin-top: 6px;
  background: linear-gradient(90deg, #d4af37 0%, #ffe08a 54%, #ffffff 100%);
  -webkit-background-clip: text;
  color: transparent;
}

.hero-slogan {
  margin-top: 18px;
  font-size: 15px;
  line-height: 1.85;
  font-weight: 900;
  color: #f5f7ff;
}

.hero-desc {
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.95;
  color: #9fb3d7;
}

.primary-btn {
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 18px;
  font-size: 15px;
  font-weight: 950;
  color: #080b12;
  background: linear-gradient(135deg, #d4af37 0%, #ffe08a 100%);
  box-shadow: 0 16px 36px rgba(212,175,55,.28);
}

.hero-content .primary-btn {
  margin-top: 22px;
}

.arbitrage-orbit {
  position: absolute;
  z-index: 2;
  right: 28px;
  top: 34px;
  width: 142px;
  height: 142px;
  pointer-events: none;
  opacity: .92;
}

.orbit-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid rgba(212,175,55,.26);
}

.ring-a {
  animation: orbitRotate 14s linear infinite;
}

.ring-b {
  inset: 18px;
  border-color: rgba(59,130,246,.25);
  animation: orbitRotateReverse 10s linear infinite;
}

.ring-c {
  inset: 36px;
  border-color: rgba(255,255,255,.12);
}

.orbit-core {
  position: absolute;
  inset: 47px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #080b12;
  font-size: 33px;
  font-weight: 950;
  background:
    radial-gradient(circle at 28% 20%, #fff5c7, transparent 25%),
    linear-gradient(135deg, #d4af37, #ffe08a);
  box-shadow: 0 0 32px rgba(212,175,55,.48);
  animation: btcFloat 4.8s ease-in-out infinite;
}

.orbit-dot {
  position: absolute;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.dot-a {
  right: 8px;
  top: 44px;
  background: #d4af37;
  box-shadow: 0 0 16px rgba(212,175,55,.9);
  animation: dotPulse 2.6s ease-in-out infinite;
}

.dot-b {
  left: 18px;
  bottom: 30px;
  background: #3b82f6;
  box-shadow: 0 0 16px rgba(59,130,246,.9);
  animation: dotPulse 2.6s ease-in-out infinite .8s;
}

@keyframes orbitRotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes orbitRotateReverse {
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
}

@keyframes btcFloat {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-7px) scale(1.04); }
}

@keyframes dotPulse {
  0%, 100% { transform: scale(.9); opacity: .6; }
  50% { transform: scale(1.25); opacity: 1; }
}

.hero-metrics {
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 11px;
}

.hero-metrics div,
.engine-card,
.section-card,
.stat-card,
.quick-card {
  background: linear-gradient(180deg, rgba(18, 28, 48, .88), rgba(8, 14, 26, .92));
  border: 1px solid rgba(148,163,184,.15);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.06), 0 18px 48px rgba(0,0,0,.26);
}

.hero-metrics div {
  padding: 14px 13px;
  border-radius: 18px;
}

.hero-metrics span,
.stat-card span,
.invite-kpis span {
  display: block;
  font-size: 12px;
  color: #8ea4c8;
}

.hero-metrics strong {
  display: block;
  margin-top: 7px;
  font-size: 19px;
}

.engine-card,
.section-card {
  margin-top: 14px;
  border-radius: 28px;
  padding: 18px 14px;
}

.engine-head,
.section-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.engine-head h2,
.section-head h2 {
  margin: 0;
  font-size: 22px;
}

.engine-head p,
.section-head p {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.7;
  color: #8ea4c8;
}

.engine-head span,
.section-head > span {
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  color: #22c55e;
  background: rgba(34,197,94,.12);
  font-size: 11px;
  font-weight: 950;
}

.engine-route {
  margin-top: 16px;
  display: grid;
  grid-template-columns: 1fr 18px 1fr 18px 1fr;
  align-items: center;
  gap: 6px;
}

.engine-route div {
  padding: 12px 10px;
  border-radius: 16px;
  background: rgba(255,255,255,.055);
  border: 1px solid rgba(255,255,255,.07);
}

.engine-route small {
  display: block;
  color: #8ea4c8;
  font-size: 11px;
}

.engine-route strong {
  display: block;
  margin-top: 6px;
  font-size: 13px;
}

.engine-route .active {
  background: rgba(212,175,55,.12);
  border-color: rgba(212,175,55,.3);
}

.engine-route .active strong {
  color: #ffe08a;
}

.engine-route i {
  height: 1px;
  background: linear-gradient(90deg, transparent, #d4af37, transparent);
}

.engine-grid {
  margin-top: 12px;
  display: grid;
  gap: 10px;
}

.engine-grid div {
  padding: 14px;
  border-radius: 18px;
  background: rgba(255,255,255,.055);
  border: 1px solid rgba(255,255,255,.07);
}

.engine-grid b,
.quick-card strong {
  display: block;
  font-size: 15px;
}

.engine-grid span,
.quick-card p {
  display: block;
  margin-top: 8px;
  color: #8ea4c8;
  font-size: 13px;
}

.invite-card {
  background:
    radial-gradient(circle at 100% 0%, rgba(212,175,55,.18), transparent 38%),
    linear-gradient(180deg, rgba(18, 28, 48, .92), rgba(8, 14, 26, .92));
}

.invite-link {
  margin-top: 14px;
  padding: 15px;
  border-radius: 18px;
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.08);
  color: #fff;
  font-size: 13px;
  word-break: break-all;
}

.invite-btn {
  margin-top: 14px;
}

.invite-kpis,
.stats-grid,
.quick-grid {
  margin-top: 14px;
  display: grid;
  gap: 12px;
}

.invite-kpis {
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.invite-kpis div {
  padding: 14px 10px;
  border-radius: 16px;
  background: rgba(255,255,255,.06);
}

.invite-kpis strong {
  display: block;
  margin-top: 7px;
  font-size: 20px;
}

.stats-grid,
.quick-grid {
  grid-template-columns: repeat(2, 1fr);
}

.stat-card {
  border-radius: 22px;
  padding: 16px 14px;
}

.stat-card strong {
  display: block;
  margin-top: 12px;
  font-size: 25px;
}

.stat-card em {
  display: block;
  margin-top: 7px;
  color: #ffe08a;
  font-style: normal;
  font-size: 12px;
}

.stat-card.highlight {
  background:
    radial-gradient(circle at 100% 0%, rgba(212,175,55,.22), transparent 42%),
    linear-gradient(180deg, rgba(30, 25, 12, .92), rgba(13, 25, 44, .88));
}

.section-head > span {
  color: #ffe08a;
  background: rgba(212,175,55,.12);
}

.model-grid {
  margin-top: 16px;
  display: grid;
  gap: 12px;
}

.model-card {
  padding: 18px;
  border-radius: 22px;
}

.model-card small {
  color: #9fb3d7;
}

.model-card strong {
  display: block;
  margin-top: 10px;
  font-size: 42px;
}

.model-card p {
  margin: 8px 0 0;
  line-height: 1.7;
  font-size: 13px;
  color: #b7c5df;
}

.model-card.fee {
  background: linear-gradient(135deg, rgba(212,175,55,.2), rgba(212,175,55,.06));
}

.model-card.pool {
  background: linear-gradient(135deg, rgba(37,99,235,.24), rgba(37,99,235,.06));
}

.quick-card {
  border-radius: 22px;
  padding: 18px 14px;
}

.quick-card p {
  line-height: 1.6;
}


/* =========================================================
 * AI Strategy Execution Terminal
 * ========================================================= */
.execution-terminal {
  margin-top: 18px;
  overflow: hidden;
  border-radius: 24px;
  background:
    radial-gradient(circle at 100% 0%, rgba(59, 130, 246, .08), transparent 34%),
    #020406;
  border: 1px solid rgba(255, 255, 255, .14);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .06),
    0 22px 54px rgba(0, 0, 0, .42);
}

.terminal-toolbar {
  min-height: 62px;
  padding: 12px 13px;
  display: flex;
  align-items: center;
  gap: 11px;
  border-bottom: 1px solid rgba(255, 255, 255, .1);
  background: linear-gradient(180deg, rgba(255, 255, 255, .055), rgba(255, 255, 255, .015));
}

.terminal-dots {
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
}

.terminal-dots i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, .34);
}

.terminal-dots i:first-child {
  background: #ef4444;
}

.terminal-dots i:nth-child(2) {
  background: #f59e0b;
}

.terminal-dots i:last-child {
  background: #22c55e;
}

.terminal-title {
  min-width: 0;
  flex: 1;
}

.terminal-title strong,
.terminal-title span {
  display: block;
}

.terminal-title strong {
  overflow: hidden;
  color: #fff;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: .7px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.terminal-title span {
  margin-top: 4px;
  color: #687386;
  font-size: 9px;
  letter-spacing: .5px;
}

.terminal-status,
.log-streaming {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #75f3a3;
  font-size: 9px;
  font-weight: 950;
  letter-spacing: .5px;
  white-space: nowrap;
}

.terminal-status i,
.log-streaming i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 12px rgba(34, 197, 94, .9);
  animation: terminalBlink 1.4s ease-in-out infinite;
}

@keyframes terminalBlink {
  0%, 100% {
    opacity: .45;
    transform: scale(.88);
  }
  50% {
    opacity: 1;
    transform: scale(1.16);
  }
}

.terminal-summary {
  padding: 12px;
  display: grid;
  gap: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, .08);
}

.terminal-summary div {
  min-width: 0;
  padding: 11px 12px;
  border-radius: 13px;
  background: rgba(255, 255, 255, .032);
  border: 1px solid rgba(255, 255, 255, .07);
}

.terminal-summary small,
.terminal-summary strong {
  display: block;
}

.terminal-summary small {
  color: #657184;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: .6px;
}

.terminal-summary strong {
  margin-top: 6px;
  overflow: hidden;
  color: #f8fafc;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 12px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.terminal-summary .risk-pass {
  color: #75f3a3;
}

.professional-route {
  padding: 13px 12px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 72px minmax(0, 1fr);
  gap: 8px;
  align-items: stretch;
}

.venue-card {
  min-width: 0;
  padding: 13px 11px;
  border-radius: 16px;
  background: rgba(255, 255, 255, .035);
  border: 1px solid rgba(255, 255, 255, .09);
}

.buy-venue {
  box-shadow: inset 3px 0 0 #3b82f6;
}

.sell-venue {
  box-shadow: inset -3px 0 0 #22c55e;
}

.venue-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
}

.venue-top span,
.venue-top em {
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 8px;
  font-style: normal;
  font-weight: 900;
  letter-spacing: .5px;
}

.venue-top span {
  color: #758197;
}

.venue-top em {
  color: #75f3a3;
}

.venue-card > strong {
  display: block;
  margin-top: 13px;
  overflow: hidden;
  color: #fff;
  font-size: 18px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.venue-card > p {
  margin: 4px 0 0;
  color: #657184;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 9px;
}

.venue-price {
  margin-top: 14px;
}

.venue-price small,
.venue-price b,
.venue-price span {
  display: block;
}

.venue-price small {
  color: #657184;
  font-size: 9px;
}

.venue-price b {
  margin-top: 5px;
  color: #fff;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 14px;
  letter-spacing: -.3px;
}

.venue-price span {
  margin-top: 3px;
  color: #657184;
  font-size: 8px;
}

.venue-volume {
  margin-top: 11px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  color: #aab5c7;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 8px;
}

.route-core {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.route-line {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
}

.route-line i {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(212, 175, 55, .9));
}

.route-line i:last-child {
  background: linear-gradient(90deg, rgba(212, 175, 55, .9), transparent);
}

.route-line span {
  width: 27px;
  height: 27px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #07090d;
  background: #ffe08a;
  box-shadow: 0 0 18px rgba(212, 175, 55, .32);
  font-size: 8px;
  font-weight: 950;
}

.route-core small,
.route-core strong,
.route-core em {
  display: block;
}

.route-core small {
  margin-top: 12px;
  color: #657184;
  font-size: 8px;
}

.route-core strong {
  margin-top: 5px;
  color: #75f3a3;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 13px;
}

.route-core em {
  margin-top: 5px;
  color: #7b879a;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 8px;
  font-style: normal;
}

.execution-metrics {
  padding: 0 12px 13px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.execution-metrics div {
  min-width: 0;
  padding: 12px;
  border-radius: 14px;
  background: #080b10;
  border: 1px solid rgba(255, 255, 255, .08);
}

.execution-metrics span,
.execution-metrics strong,
.execution-metrics em {
  display: block;
}

.execution-metrics span {
  color: #657184;
  font-size: 9px;
}

.execution-metrics strong {
  margin-top: 7px;
  overflow: hidden;
  color: #f8fafc;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 14px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.execution-metrics em {
  margin-top: 4px;
  color: #657184;
  font-size: 8px;
  font-style: normal;
}

.execution-metrics .profit-metric {
  background: linear-gradient(145deg, rgba(34, 197, 94, .1), rgba(255, 255, 255, .025));
  border-color: rgba(34, 197, 94, .25);
}

.execution-metrics .profit-metric strong {
  color: #75f3a3;
}

.strategy-log {
  margin: 0 12px 13px;
  overflow: hidden;
  border-radius: 16px;
  background: #000;
  border: 1px solid rgba(255, 255, 255, .1);
}

.strategy-log-head {
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, .09);
  background: rgba(255, 255, 255, .025);
}

.strategy-log-head strong,
.strategy-log-head span {
  display: block;
}

.strategy-log-head strong {
  color: #fff;
  font-size: 11px;
}

.strategy-log-head span {
  margin-top: 4px;
  color: #606a7a;
  font-size: 8px;
}

.log-window {
  height: 238px;
  overflow: hidden;
  padding: 5px 0;
}

.log-row {
  min-height: 29px;
  padding: 6px 10px;
  display: grid;
  grid-template-columns: 78px 48px minmax(0, 1fr);
  gap: 7px;
  align-items: start;
  border-bottom: 1px solid rgba(255, 255, 255, .035);
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
}

.log-row time {
  color: #5d6878;
  font-size: 8px;
  line-height: 1.55;
}

.log-level {
  padding: 2px 4px;
  border-radius: 4px;
  color: #fff;
  background: rgba(255, 255, 255, .08);
  font-size: 7px;
  font-weight: 950;
  text-align: center;
}

.level-market {
  color: #93c5fd;
  background: rgba(59, 130, 246, .13);
}

.level-signal {
  color: #fde68a;
  background: rgba(212, 175, 55, .14);
}

.level-risk {
  color: #c4b5fd;
  background: rgba(139, 92, 246, .14);
}

.level-buy {
  color: #93c5fd;
  background: rgba(59, 130, 246, .14);
}

.level-sell,
.level-settle {
  color: #86efac;
  background: rgba(34, 197, 94, .14);
}

.log-row code {
  color: #e5e7eb;
  font-family: inherit;
  font-size: 8px;
  line-height: 1.55;
  word-break: break-word;
}

.terminal-footer {
  padding: 10px 12px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 7px 14px;
  color: #566173;
  border-top: 1px solid rgba(255, 255, 255, .07);
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 8px;
}

.terminal-footer b {
  color: #a8b2c2;
  font-weight: 700;
}


@media (min-width: 768px) {
  .page-body {
    max-width: 1180px;
    margin: 0 auto;
  }

  .hero-premium {
    padding: 52px 44px 36px;
    min-height: 620px;
  }

  .hero-premium h1 {
    font-size: 62px;
  }

  .arbitrage-orbit {
    width: 220px;
    height: 220px;
    right: 70px;
    top: 64px;
  }

  .orbit-core {
    inset: 74px;
    font-size: 52px;
  }

  .ring-b {
    inset: 28px;
  }

  .ring-c {
    inset: 56px;
  }

  .hero-metrics,
  .engine-grid,
  .stats-grid,
  .quick-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .model-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .terminal-summary {
    grid-template-columns: 1.2fr 1fr 1fr;
  }

  .professional-route {
    grid-template-columns: minmax(0, 1fr) 130px minmax(0, 1fr);
    padding: 18px;
    gap: 14px;
  }

  .venue-card {
    padding: 18px;
  }

  .venue-card > strong {
    font-size: 25px;
  }

  .venue-price b {
    font-size: 22px;
  }

  .route-core strong {
    font-size: 18px;
  }

  .execution-metrics {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    padding: 0 18px 18px;
  }

  .strategy-log {
    margin: 0 18px 18px;
  }

  .log-row {
    grid-template-columns: 110px 64px minmax(0, 1fr);
  }

  .log-row time,
  .log-row code {
    font-size: 10px;
  }

  .log-level {
    font-size: 9px;
  }
}


/* =========================================================
 * BCH Home API states
 * ========================================================= */
.home-loading,
.home-error {
  position: sticky;
  z-index: 30;
  top: 10px;
  margin: 10px 0 0;
  min-height: 42px;
  padding: 0 14px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .25px;
  backdrop-filter: blur(14px);
}

.home-loading {
  color: #dbeafe;
  background: rgba(8, 18, 36, .88);
  border: 1px solid rgba(59, 130, 246, .28);
}

.home-loading i {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #3b82f6;
  box-shadow: 0 0 16px rgba(59, 130, 246, .9);
  animation: terminalBlink 1.1s ease-in-out infinite;
}

.home-error {
  justify-content: space-between;
  color: #fecaca;
  background: rgba(69, 10, 10, .84);
  border: 1px solid rgba(239, 68, 68, .3);
}

.home-error button {
  height: 28px;
  padding: 0 12px;
  border: 0;
  border-radius: 9px;
  color: #170707;
  background: #fecaca;
  font-size: 10px;
  font-weight: 900;
}

.engine-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.engine-head-actions > span.syncing {
  color: #93c5fd;
  background: rgba(59, 130, 246, .13);
}

.engine-head-actions > span.standby {
  color: #cbd5e1;
  background: rgba(148, 163, 184, .12);
}

.engine-refresh {
  width: 30px;
  height: 30px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, .1);
  color: #a8b2c2;
  background: rgba(255, 255, 255, .045);
  font-size: 18px;
  line-height: 28px;
  cursor: pointer;
}

.engine-refresh:disabled {
  cursor: default;
  opacity: .65;
}

.engine-refresh.rotating {
  animation: apiRefreshRotate .85s linear infinite;
}

@keyframes apiRefreshRotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.terminal-status.offline,
.terminal-status.offline i {
  color: #94a3b8;
}

.terminal-status.offline i {
  background: #64748b;
  box-shadow: 0 0 10px rgba(100, 116, 139, .55);
}

.log-empty {
  height: 100%;
  min-height: 190px;
  padding: 28px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.log-empty strong {
  color: #d7dde7;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 10px;
}

.log-empty span {
  max-width: 420px;
  margin-top: 8px;
  color: #596476;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 8px;
  line-height: 1.7;
}

.level-info {
  color: #cbd5e1;
  background: rgba(148, 163, 184, .12);
}

.level-fail,
.level-error {
  color: #fca5a5;
  background: rgba(239, 68, 68, .14);
}


/* =========================================================
 * BCH Invite Relation Binding Dialog
 * ========================================================= */
.bind-modal-mask {
  position: fixed;
  z-index: 1200;
  inset: 0;
  padding: 22px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  background:
    radial-gradient(circle at 50% 16%, rgba(212, 175, 55, .14), transparent 32%),
    rgba(1, 4, 10, .84);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

.bind-relation-dialog {
  position: relative;
  width: 100%;
  max-width: 420px;
  overflow: hidden;
  border-radius: 30px;
  background:
    radial-gradient(circle at 85% 3%, rgba(212, 175, 55, .2), transparent 30%),
    radial-gradient(circle at 0% 100%, rgba(37, 99, 235, .18), transparent 36%),
    linear-gradient(155deg, #101a2c 0%, #070d17 48%, #04070d 100%);
  border: 1px solid rgba(255, 224, 138, .22);
  box-shadow:
    0 34px 90px rgba(0, 0, 0, .7),
    inset 0 1px 0 rgba(255, 255, 255, .09);
}

.bind-dialog-grid {
  position: absolute;
  inset: 0;
  opacity: .48;
  background-image:
    linear-gradient(rgba(255, 255, 255, .025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, .025) 1px, transparent 1px);
  background-size: 28px 28px;
  pointer-events: none;
}

.bind-dialog-glow {
  position: absolute;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  filter: blur(60px);
  pointer-events: none;
}

.bind-dialog-glow-gold {
  top: -90px;
  right: -70px;
  background: rgba(212, 175, 55, .28);
}

.bind-dialog-glow-blue {
  left: -90px;
  bottom: -95px;
  background: rgba(37, 99, 235, .23);
}

.bind-dialog-content {
  position: relative;
  z-index: 2;
  padding: 30px 22px 22px;
}

.bind-dialog-icon {
  position: relative;
  width: 70px;
  height: 70px;
  margin: 0 auto 18px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #090b10;
  background:
    radial-gradient(circle at 28% 20%, #fff7cf, transparent 30%),
    linear-gradient(135deg, #c99e27 0%, #ffe08a 62%, #fff4c7 100%);
  box-shadow: 0 18px 38px rgba(212, 175, 55, .25), 0 0 34px rgba(212, 175, 55, .2);
}

.bind-dialog-icon span {
  font-size: 39px;
  font-weight: 950;
}

.bind-dialog-icon i {
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 21px;
  height: 21px;
  border: 4px solid #09101b;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 15px rgba(34, 197, 94, .72);
}

.bind-dialog-badge {
  width: fit-content;
  margin: 0 auto;
  padding: 7px 11px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 7px;
  color: #ffe8a8;
  background: rgba(212, 175, 55, .1);
  border: 1px solid rgba(212, 175, 55, .25);
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: .6px;
}

.bind-dialog-badge i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #facc15;
  box-shadow: 0 0 10px rgba(250, 204, 21, .9);
}

.bind-dialog-content h3 {
  margin: 17px 0 0;
  color: #fff;
  font-size: 25px;
  font-weight: 950;
  letter-spacing: -.45px;
  text-align: center;
}

.bind-dialog-desc {
  margin: 11px auto 0;
  max-width: 345px;
  color: #9eafca;
  font-size: 13px;
  line-height: 1.8;
  text-align: center;
}

.bind-address-field {
  margin-top: 22px;
}

.bind-address-field label {
  display: block;
  margin-bottom: 8px;
  color: #d9e2f1;
  font-size: 12px;
  font-weight: 850;
}

.bind-address-input-wrap {
  height: 58px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 9px;
  border-radius: 17px;
  background: rgba(255, 255, 255, .055);
  border: 1px solid rgba(255, 224, 138, .18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .04);
}

.bind-address-prefix,
.bind-address-lock {
  flex: 0 0 auto;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 8px;
  font-weight: 950;
  letter-spacing: .5px;
}

.bind-address-prefix {
  padding: 5px 6px;
  border-radius: 7px;
  color: #0b1019;
  background: #ffe08a;
}

.bind-address-lock {
  color: #8290a6;
}

.bind-address-input-wrap input {
  min-width: 0;
  flex: 1;
  padding: 0;
  border: 0;
  outline: 0;
  color: #fff;
  background: transparent;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: .2px;
  cursor: default;
  caret-color: transparent;
}

.bind-address-field small {
  display: block;
  margin-top: 8px;
  color: #687893;
  font-size: 10px;
}

.bind-security-note {
  margin-top: 15px;
  padding: 12px;
  display: flex;
  align-items: flex-start;
  gap: 9px;
  border-radius: 15px;
  color: #a9b7cc;
  background: rgba(34, 197, 94, .065);
  border: 1px solid rgba(34, 197, 94, .15);
}

.bind-security-note > span {
  width: 19px;
  height: 19px;
  flex: 0 0 19px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #07120b;
  background: #75f3a3;
  font-size: 11px;
  font-weight: 950;
}

.bind-security-note p {
  margin: 0;
  font-size: 10px;
  line-height: 1.65;
}

.bind-security-note b {
  color: #dbe7f8;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
}

.bind-confirm-btn {
  width: 100%;
  height: 55px;
  margin-top: 18px;
  border: 0;
  border-radius: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  color: #080b11;
  background: linear-gradient(135deg, #c99e27 0%, #ffe08a 58%, #fff0b7 100%);
  box-shadow: 0 16px 34px rgba(212, 175, 55, .24);
  font-size: 14px;
  font-weight: 950;
  cursor: pointer;
  transition: transform .18s ease, opacity .18s ease, box-shadow .18s ease;
}

.bind-confirm-btn:active:not(:disabled) {
  transform: scale(.985);
}

.bind-confirm-btn:disabled {
  opacity: .66;
  cursor: default;
  box-shadow: none;
}

.bind-btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(8, 11, 17, .24);
  border-top-color: #080b11;
  border-radius: 50%;
  animation: bindSpinnerRotate .75s linear infinite;
}

@keyframes bindSpinnerRotate {
  to { transform: rotate(360deg); }
}

.bind-dialog-footnote {
  margin: 13px 0 0;
  color: #5f6c80;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 8px;
  letter-spacing: .35px;
  text-align: center;
}

.bind-modal-fade-enter-active,
.bind-modal-fade-leave-active {
  transition: opacity .22s ease;
}

.bind-modal-fade-enter-active .bind-relation-dialog,
.bind-modal-fade-leave-active .bind-relation-dialog {
  transition: transform .26s ease, opacity .22s ease;
}

.bind-modal-fade-enter,
.bind-modal-fade-leave-to {
  opacity: 0;
}

.bind-modal-fade-enter .bind-relation-dialog,
.bind-modal-fade-leave-to .bind-relation-dialog {
  opacity: 0;
  transform: translateY(16px) scale(.97);
}

/* =========================================================
 * Built-in Toast
 * ========================================================= */
.home-local-toast {
  position: fixed;
  z-index: 1400;
  top: calc(18px + env(safe-area-inset-top));
  left: 50%;
  width: max-content;
  max-width: calc(100vw - 32px);
  min-height: 46px;
  padding: 10px 15px 10px 10px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  gap: 9px;
  transform: translateX(-50%);
  color: #effaf3;
  background: rgba(9, 22, 16, .96);
  border: 1px solid rgba(34, 197, 94, .28);
  box-shadow: 0 18px 50px rgba(0, 0, 0, .46);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  font-size: 12px;
  font-weight: 850;
  line-height: 1.45;
  text-align: left;
}

.home-toast-icon {
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #06110a;
  background: #75f3a3;
  font-size: 13px;
  font-weight: 950;
}

.home-local-toast.toast-warning {
  color: #fff6d5;
  background: rgba(39, 29, 8, .96);
  border-color: rgba(250, 204, 21, .3);
}

.home-local-toast.toast-warning .home-toast-icon {
  color: #231a04;
  background: #fde68a;
}

.home-local-toast.toast-error {
  color: #fee2e2;
  background: rgba(39, 8, 11, .96);
  border-color: rgba(239, 68, 68, .32);
}

.home-local-toast.toast-error .home-toast-icon {
  color: #2b0707;
  background: #fca5a5;
}

.home-toast-fade-enter-active,
.home-toast-fade-leave-active {
  transition: opacity .2s ease, transform .2s ease;
}

.home-toast-fade-enter,
.home-toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -12px);
}

@media (max-width: 420px) {
  .bind-modal-mask {
    padding: 14px 12px;
    align-items: flex-end;
  }

  .bind-relation-dialog {
    max-width: none;
    border-radius: 28px 28px 22px 22px;
  }

  .bind-dialog-content {
    padding: 25px 17px 18px;
  }

  .bind-dialog-icon {
    width: 62px;
    height: 62px;
    border-radius: 21px;
  }

  .bind-dialog-icon span {
    font-size: 34px;
  }

  .bind-dialog-content h3 {
    font-size: 22px;
  }

  .bind-address-input-wrap {
    padding: 0 10px;
    gap: 7px;
  }

  .bind-address-input-wrap input {
    font-size: 14px;
  }
}



/* =========================================================
 * BCH Professional Visual Upgrade
 * 机构级深色终端视觉：低饱和金色 + 科技蓝 + 审慎动效
 * ========================================================= */

.bch-home {
  position: relative;
  isolation: isolate;
  overflow-x: hidden;
  background:
    radial-gradient(circle at 50% -10%, rgba(214, 177, 63, .18), transparent 26%),
    radial-gradient(circle at 105% 22%, rgba(41, 108, 255, .18), transparent 31%),
    radial-gradient(circle at -8% 68%, rgba(22, 78, 164, .13), transparent 30%),
    linear-gradient(180deg, #03060c 0%, #07101d 42%, #091321 100%);
}

.bch-home::before {
  content: "";
  position: fixed;
  z-index: -2;
  inset: 0;
  opacity: .34;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(118, 151, 201, .032) 1px, transparent 1px),
    linear-gradient(90deg, rgba(118, 151, 201, .032) 1px, transparent 1px);
  background-size: 36px 36px;
  mask-image: linear-gradient(180deg, #000 0%, rgba(0,0,0,.78) 45%, transparent 100%);
  animation: institutionalGridMove 28s linear infinite;
}

.bch-home::after {
  content: "";
  position: fixed;
  z-index: -1;
  top: 18%;
  left: 50%;
  width: 560px;
  height: 560px;
  border-radius: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  background: radial-gradient(circle, rgba(27, 94, 202, .095), transparent 66%);
  filter: blur(12px);
  animation: ambientFieldFloat 11s ease-in-out infinite;
}

.page-body {
  position: relative;
  z-index: 1;
}

/* Hero：增加机构级数据舱质感 */
.hero-premium {
  isolation: isolate;
  border-color: rgba(151, 174, 214, .13);
  background:
    radial-gradient(circle at 80% 17%, rgba(214, 177, 63, .22), transparent 25%),
    radial-gradient(circle at 11% 84%, rgba(36, 101, 226, .25), transparent 38%),
    linear-gradient(148deg, #03060c 0%, #08111f 48%, #101a2a 100%);
  box-shadow:
    0 34px 90px rgba(0, 0, 0, .52),
    inset 0 1px 0 rgba(255,255,255,.055),
    inset 0 -1px 0 rgba(60, 130, 255, .08);
  animation: premiumSectionReveal .72s cubic-bezier(.2,.75,.25,1) both;
}

.hero-premium::before {
  content: "";
  position: absolute;
  z-index: 1;
  top: -35%;
  bottom: -35%;
  left: -32%;
  width: 24%;
  transform: skewX(-19deg);
  pointer-events: none;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255,255,255,.02),
    rgba(255,226,145,.08),
    rgba(255,255,255,.025),
    transparent
  );
  animation: heroOpticalSweep 8.8s ease-in-out infinite;
}

.hero-premium::after {
  content: "";
  position: absolute;
  z-index: 3;
  top: 0;
  left: 8%;
  right: 8%;
  height: 1px;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(75, 143, 255, .55),
    rgba(255, 224, 138, .72),
    rgba(75, 143, 255, .55),
    transparent
  );
  background-size: 220% 100%;
  animation: borderEnergyFlow 5.6s linear infinite;
}

.hero-grid-bg {
  animation: heroGridDrift 24s linear infinite;
}

.hero-glow-gold {
  animation: premiumGlowGold 7s ease-in-out infinite;
}

.hero-glow-blue {
  animation: premiumGlowBlue 9s ease-in-out infinite;
}

.hero-content {
  animation: contentRiseIn .76s .06s cubic-bezier(.2,.75,.25,1) both;
}

.hero-badge {
  position: relative;
  overflow: hidden;
  letter-spacing: .3px;
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.08),
    0 10px 28px rgba(0,0,0,.16);
}

.hero-badge::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: -42px;
  width: 28px;
  transform: skewX(-22deg);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.3), transparent);
  animation: badgeOpticalSweep 5.5s ease-in-out infinite;
}

.hero-badge i {
  animation: institutionalStatusPulse 1.9s ease-in-out infinite;
}

.hero-premium h1 {
  text-shadow:
    0 8px 30px rgba(0,0,0,.5),
    0 0 32px rgba(68, 127, 237, .08);
}

.hero-premium h1 span {
  background-size: 200% 100%;
  animation: premiumTextFlow 6.5s linear infinite;
}

/* 主按钮：避免娱乐化，采用精细金属扫光 */
.primary-btn {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  isolation: isolate;
  border: 1px solid rgba(255, 243, 194, .58);
  background:
    linear-gradient(135deg, #bd9222 0%, #e0bc55 38%, #ffe49a 66%, #c79c2b 100%);
  background-size: 180% 180%;
  box-shadow:
    0 16px 36px rgba(197, 151, 34, .24),
    inset 0 1px 0 rgba(255,255,255,.62),
    inset 0 -1px 0 rgba(95,66,5,.18);
  transition: transform .2s ease, box-shadow .2s ease, background-position .25s ease;
}

.primary-btn::before {
  content: "";
  position: absolute;
  z-index: -1;
  top: -18px;
  bottom: -18px;
  left: -54px;
  width: 36px;
  transform: skewX(-22deg);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.68), transparent);
  animation: actionButtonSweep 5.2s ease-in-out infinite;
}

.primary-btn:hover {
  transform: translateY(-2px);
  background-position: 100% 50%;
  box-shadow:
    0 21px 44px rgba(197, 151, 34, .3),
    inset 0 1px 0 rgba(255,255,255,.72);
}

.primary-btn:active {
  transform: scale(.985);
}

/* 风险披露：专业金融页面必须明确披露市场风险 */
.risk-disclosure {
  position: relative;
  margin-top: 14px;
  padding: 13px 13px 13px 12px;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  overflow: hidden;
  border-radius: 17px;
  color: #b7c5db;
  background:
    linear-gradient(135deg, rgba(14, 25, 43, .88), rgba(7, 14, 26, .92));
  border: 1px solid rgba(117, 151, 204, .16);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.045),
    0 12px 30px rgba(0,0,0,.16);
}

.risk-disclosure::before {
  content: "";
  position: absolute;
  left: 0;
  top: 15%;
  bottom: 15%;
  width: 2px;
  border-radius: 3px;
  background: linear-gradient(180deg, transparent, #d7b54c, transparent);
  box-shadow: 0 0 12px rgba(215,181,76,.35);
}

.risk-disclosure-icon {
  width: 28px;
  height: 28px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f7dfa0;
  background: rgba(215,181,76,.1);
  border: 1px solid rgba(215,181,76,.2);
  font-family: Georgia, serif;
  font-size: 14px;
  font-weight: 900;
}

.risk-disclosure-copy {
  min-width: 0;
}

.risk-disclosure-copy strong,
.risk-disclosure-copy p {
  display: block;
}

.risk-disclosure-copy strong {
  color: #e7edf8;
  font-size: 11px;
  font-weight: 900;
}

.risk-disclosure-copy p {
  margin: 4px 0 0;
  color: #8495af;
  font-size: 9px;
  line-height: 1.6;
}

.risk-disclosure > span {
  padding: 5px 7px;
  border-radius: 7px;
  color: #c5a951;
  background: rgba(215,181,76,.07);
  border: 1px solid rgba(215,181,76,.14);
  font-family: SFMono-Regular, Consolas, "Liberation Mono", monospace;
  font-size: 7px;
  font-weight: 900;
  letter-spacing: .45px;
  white-space: nowrap;
}

/* 卡片体系：玻璃、边缘能量线与悬浮深度 */
.hero-metrics div,
.engine-card,
.section-card,
.stat-card,
.quick-card {
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-color: rgba(137, 163, 206, .15);
  background:
    radial-gradient(circle at 100% 0%, rgba(43, 104, 209, .06), transparent 34%),
    linear-gradient(180deg, rgba(17, 28, 48, .9), rgba(7, 14, 26, .94));
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.055),
    0 20px 52px rgba(0,0,0,.28);
}

.engine-card::before,
.section-card::before,
.stat-card::before,
.quick-card::before {
  content: "";
  position: absolute;
  z-index: 0;
  top: 0;
  left: 10%;
  right: 10%;
  height: 1px;
  opacity: .58;
  background: linear-gradient(90deg, transparent, rgba(77, 145, 255, .56), rgba(255,224,138,.48), transparent);
  background-size: 200% 100%;
  animation: borderEnergyFlow 7s linear infinite;
}

.engine-card > *,
.section-card > *,
.stat-card > *,
.quick-card > * {
  position: relative;
  z-index: 1;
}

.engine-card,
.section-card,
.stat-card,
.quick-card,
.hero-metrics div,
.model-card,
.venue-card {
  transition:
    transform .24s ease,
    border-color .24s ease,
    box-shadow .24s ease,
    background .24s ease;
}

.hero-metrics div:hover,
.stat-card:hover,
.quick-card:hover,
.model-card:hover,
.venue-card:hover {
  transform: translateY(-4px);
  border-color: rgba(100, 153, 236, .28);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.075),
    0 24px 58px rgba(0,0,0,.36),
    0 0 28px rgba(38, 101, 206, .07);
}

.hero-metrics div {
  animation: dataTileRise .6s cubic-bezier(.2,.75,.25,1) both;
}

.hero-metrics div:nth-child(1) { animation-delay: .12s; }
.hero-metrics div:nth-child(2) { animation-delay: .18s; }
.hero-metrics div:nth-child(3) { animation-delay: .24s; }
.hero-metrics div:nth-child(4) { animation-delay: .30s; }

.engine-card {
  animation: premiumSectionReveal .7s .14s cubic-bezier(.2,.75,.25,1) both;
}

.invite-card {
  background:
    radial-gradient(circle at 100% 0%, rgba(214,177,63,.14), transparent 34%),
    radial-gradient(circle at 0% 100%, rgba(37,99,235,.1), transparent 38%),
    linear-gradient(180deg, rgba(17, 28, 48, .93), rgba(7, 14, 26, .95));
}

.invite-link {
  position: relative;
  overflow: hidden;
  color: #e9f1ff;
  background:
    linear-gradient(135deg, rgba(255,255,255,.055), rgba(255,255,255,.026));
  border-color: rgba(123, 157, 209, .15);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.04);
}

.invite-link::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: -50px;
  width: 30px;
  transform: skewX(-22deg);
  background: linear-gradient(90deg, transparent, rgba(103,164,255,.14), transparent);
  animation: dataFieldSweep 7.2s ease-in-out infinite;
}

.invite-kpis div,
.engine-grid div,
.engine-route div {
  background:
    linear-gradient(145deg, rgba(255,255,255,.052), rgba(255,255,255,.023));
  border: 1px solid rgba(137, 163, 206, .11);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.035);
}

/* 执行路线采用流动光，而不是娱乐化闪烁 */
.engine-route i {
  position: relative;
  overflow: hidden;
  background: rgba(122, 151, 194, .13);
}

.engine-route i::after {
  content: "";
  position: absolute;
  top: 0;
  left: -60%;
  width: 60%;
  height: 1px;
  background: linear-gradient(90deg, transparent, #f1d477, transparent);
  animation: executionPathFlow 2.5s linear infinite;
}

.engine-route .active {
  background:
    radial-gradient(circle at 100% 0%, rgba(215,181,76,.15), transparent 46%),
    linear-gradient(145deg, rgba(215,181,76,.11), rgba(255,255,255,.025));
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.05),
    0 0 22px rgba(215,181,76,.04);
}

/* 资产统计更像机构 Dashboard */
.stat-card strong,
.invite-kpis strong,
.model-card strong {
  font-variant-numeric: tabular-nums;
}

.stat-card strong {
  letter-spacing: -.5px;
  text-shadow: 0 5px 20px rgba(0,0,0,.32);
}

.stat-card.highlight {
  border-color: rgba(215,181,76,.2);
  background:
    radial-gradient(circle at 100% 0%, rgba(215,181,76,.19), transparent 43%),
    radial-gradient(circle at 0% 100%, rgba(36,101,226,.11), transparent 44%),
    linear-gradient(180deg, rgba(29, 26, 17, .93), rgba(10, 21, 38, .92));
}

.stat-card.highlight strong {
  color: #ffe39a;
  text-shadow: 0 0 24px rgba(215,181,76,.16);
}

/* 资金配置模型 */
.model-card {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(139, 166, 211, .13);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.045),
    0 16px 38px rgba(0,0,0,.2);
}

.model-card::after {
  content: "";
  position: absolute;
  top: -45%;
  right: -18%;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(10px);
  background: radial-gradient(circle, rgba(255,255,255,.08), transparent 65%);
  animation: modelAmbientPulse 5.5s ease-in-out infinite;
}

.model-card.fee {
  background:
    radial-gradient(circle at 100% 0%, rgba(215,181,76,.16), transparent 42%),
    linear-gradient(135deg, rgba(113,82,16,.16), rgba(17,28,48,.66));
}

.model-card.pool {
  background:
    radial-gradient(circle at 100% 0%, rgba(52,119,236,.2), transparent 42%),
    linear-gradient(135deg, rgba(27,74,158,.17), rgba(17,28,48,.66));
}

/* 专业执行终端：扫描线、审计感、细边框 */
.execution-terminal {
  position: relative;
  isolation: isolate;
  border-color: rgba(126, 158, 209, .2);
  background:
    radial-gradient(circle at 100% 0%, rgba(47, 113, 226, .1), transparent 35%),
    radial-gradient(circle at 0% 100%, rgba(214, 177, 63, .055), transparent 38%),
    linear-gradient(180deg, #020508 0%, #03070c 100%);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.06),
    0 28px 68px rgba(0,0,0,.48),
    0 0 40px rgba(28, 88, 183, .045);
}

.execution-terminal::before {
  content: "";
  position: absolute;
  z-index: 0;
  inset: 0;
  opacity: .18;
  pointer-events: none;
  background:
    repeating-linear-gradient(
      180deg,
      transparent 0,
      transparent 3px,
      rgba(113,153,216,.055) 4px
    );
}

.execution-terminal::after {
  content: "";
  position: absolute;
  z-index: 1;
  left: 0;
  right: 0;
  top: -34px;
  height: 34px;
  pointer-events: none;
  background: linear-gradient(180deg, transparent, rgba(67,135,243,.09), transparent);
  animation: terminalVerticalScan 8s linear infinite;
}

.execution-terminal > * {
  position: relative;
  z-index: 2;
}

.terminal-toolbar {
  background:
    linear-gradient(180deg, rgba(24,36,55,.66), rgba(8,14,23,.52));
  backdrop-filter: blur(12px);
}

.terminal-title strong {
  color: #eaf2ff;
  text-shadow: 0 0 16px rgba(69,135,240,.16);
}

.terminal-status,
.log-streaming {
  text-shadow: 0 0 12px rgba(34,197,94,.25);
}

.route-line span {
  animation: routeCorePulse 2.8s ease-in-out infinite;
}

.venue-card {
  background:
    linear-gradient(145deg, rgba(18,28,43,.74), rgba(4,8,13,.84));
  border-color: rgba(126, 158, 209, .13);
}

.buy-venue {
  box-shadow:
    inset 3px 0 0 #397ee5,
    inset 0 1px 0 rgba(255,255,255,.035);
}

.sell-venue {
  box-shadow:
    inset -3px 0 0 #28a96a,
    inset 0 1px 0 rgba(255,255,255,.035);
}

.execution-metrics div {
  background:
    linear-gradient(145deg, rgba(11,18,29,.96), rgba(4,8,13,.98));
  border-color: rgba(124, 154, 203, .11);
}

.strategy-log {
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.025),
    0 14px 34px rgba(0,0,0,.24);
}

.log-row {
  transition: background .18s ease;
}

.log-row:hover {
  background: rgba(58, 116, 210, .055);
}

/* 快捷入口增加方向提示 */
.quick-card {
  cursor: pointer;
  min-height: 112px;
}

.quick-card::after {
  content: "↗";
  position: absolute;
  z-index: 1;
  right: 14px;
  top: 13px;
  color: rgba(145, 177, 227, .42);
  font-size: 16px;
  transition: transform .2s ease, color .2s ease;
}

.quick-card:hover::after {
  color: #ffe08a;
  transform: translate(2px, -2px);
}

.quick-card strong {
  padding-right: 24px;
}

/* 模态框进一步统一机构风格 */
.bind-relation-dialog {
  border-color: rgba(215,181,76,.24);
  box-shadow:
    0 38px 100px rgba(0,0,0,.75),
    inset 0 1px 0 rgba(255,255,255,.08),
    0 0 40px rgba(47,107,210,.06);
}

.bind-dialog-icon {
  animation: dialogInstitutionalFloat 4.5s ease-in-out infinite;
}

/* 动画定义 */
@keyframes institutionalGridMove {
  from { background-position: 0 0, 0 0; }
  to { background-position: 36px 36px, 36px 36px; }
}

@keyframes ambientFieldFloat {
  0%, 100% { transform: translateX(-50%) translateY(0) scale(.92); opacity: .65; }
  50% { transform: translateX(-50%) translateY(-26px) scale(1.08); opacity: 1; }
}

@keyframes premiumSectionReveal {
  from { opacity: 0; transform: translateY(18px) scale(.988); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes contentRiseIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes heroOpticalSweep {
  0%, 15% { left: -32%; opacity: 0; }
  25% { opacity: .9; }
  52% { left: 115%; opacity: 0; }
  100% { left: 115%; opacity: 0; }
}

@keyframes borderEnergyFlow {
  from { background-position: 200% 0; }
  to { background-position: -20% 0; }
}

@keyframes heroGridDrift {
  from { background-position: 0 0, 0 0; }
  to { background-position: 30px 30px, 30px 30px; }
}

@keyframes premiumGlowGold {
  0%, 100% { opacity: .62; transform: scale(.9) translate(0,0); }
  50% { opacity: 1; transform: scale(1.15) translate(-12px,8px); }
}

@keyframes premiumGlowBlue {
  0%, 100% { opacity: .58; transform: scale(.92) translate(0,0); }
  50% { opacity: .95; transform: scale(1.12) translate(14px,-10px); }
}

@keyframes badgeOpticalSweep {
  0%, 25% { left: -42px; opacity: 0; }
  37% { opacity: 1; }
  58% { left: calc(100% + 42px); opacity: 0; }
  100% { left: calc(100% + 42px); opacity: 0; }
}

@keyframes premiumTextFlow {
  from { background-position: 0 50%; }
  to { background-position: 200% 50%; }
}

@keyframes actionButtonSweep {
  0%, 27% { left: -54px; opacity: 0; }
  38% { opacity: 1; }
  59% { left: calc(100% + 54px); opacity: 0; }
  100% { left: calc(100% + 54px); opacity: 0; }
}

@keyframes institutionalStatusPulse {
  0%, 100% { transform: scale(.86); opacity: .58; }
  50% { transform: scale(1.14); opacity: 1; }
}

@keyframes dataTileRise {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes dataFieldSweep {
  0%, 28% { left: -50px; opacity: 0; }
  38% { opacity: 1; }
  58% { left: calc(100% + 50px); opacity: 0; }
  100% { left: calc(100% + 50px); opacity: 0; }
}

@keyframes executionPathFlow {
  from { left: -60%; }
  to { left: 120%; }
}

@keyframes modelAmbientPulse {
  0%, 100% { opacity: .55; transform: scale(.9); }
  50% { opacity: 1; transform: scale(1.12); }
}

@keyframes terminalVerticalScan {
  0% { top: -34px; opacity: 0; }
  12% { opacity: .8; }
  72% { opacity: .5; }
  100% { top: calc(100% + 34px); opacity: 0; }
}

@keyframes routeCorePulse {
  0%, 100% { transform: scale(.94); box-shadow: 0 0 14px rgba(212,175,55,.22); }
  50% { transform: scale(1.05); box-shadow: 0 0 26px rgba(212,175,55,.38); }
}

@keyframes dialogInstitutionalFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

/* 移动端：保留高级感，但控制动效与空间 */
@media (max-width: 520px) {
  .risk-disclosure {
    grid-template-columns: 28px minmax(0, 1fr);
  }

  .risk-disclosure > span {
    display: none;
  }

  .risk-disclosure-copy p {
    font-size: 8px;
  }

  .hero-premium::before {
    width: 34%;
  }

  .quick-card {
    min-height: 118px;
  }
}

/* 用户系统启用减少动画时，取消非必要动态效果 */
@media (prefers-reduced-motion: reduce) {
  .bch-home::before,
  .bch-home::after,
  .hero-premium,
  .hero-premium::before,
  .hero-premium::after,
  .hero-grid-bg,
  .hero-glow-gold,
  .hero-glow-blue,
  .hero-content,
  .hero-badge::after,
  .hero-badge i,
  .hero-premium h1 span,
  .primary-btn::before,
  .hero-metrics div,
  .engine-card,
  .engine-card::before,
  .section-card::before,
  .stat-card::before,
  .quick-card::before,
  .invite-link::after,
  .engine-route i::after,
  .model-card::after,
  .execution-terminal::after,
  .route-line span,
  .bind-dialog-icon {
    animation: none !important;
  }
}

</style>